import React from "react";

// Maps a normalized error/stale outcome to its operator-facing presentation. The
// API's own `error` message (when present) is always shown verbatim beneath the
// heading; these add the "what now". `canRetry` comes from the normalizer, not
// from here — this only chooses copy, icon, and colour.
function errorPresentation(outcome) {
  if (outcome.stale) {
    return {
      key: "stale",
      variant: "warning",
      icon: "fa-hourglass-half",
      heading: "This merge appears stalled",
      guidance: "The merge job stopped making progress. Retry re-dispatches it to a worker.",
    };
  }

  switch (outcome.errorType) {
    case "post_commit_incomplete":
      return {
        key: "post_commit_incomplete",
        variant: "warning",
        icon: "fa-screwdriver-wrench",
        heading: "Merge committed; finishing touches incomplete",
        guidance: "The entities were merged, but some post-merge steps did not finish. Retry completes them.",
      };
    case "merge_divergence":
      return {
        key: "merge_divergence",
        variant: "danger",
        icon: "fa-code-branch",
        heading: "Merge divergence — do not blindly retry",
        // Fail-closed cross-DB state: the operator must reconcile via the
        // divergence checker before any further action, so no Retry is offered.
        guidance: "Use the divergence checker to reconcile the databases before any further action.",
      };
    case "dispatch_failed":
      return {
        key: "dispatch_failed",
        variant: "warning",
        icon: "fa-plug",
        heading: "Could not hand the merge to the worker",
        guidance: "The merge was never dispatched. Retry hands it off again.",
      };
    case "v1_unavailable":
      return {
        key: "v1_unavailable",
        variant: "warning",
        icon: "fa-plug",
        heading: "V1 is unreachable",
        guidance: "The V1 database could not be reached. Retry once V1 is back.",
      };
    case "deadline":
      return {
        key: "deadline",
        variant: "danger",
        icon: "fa-clock",
        heading: "Merge exceeded its deadline",
        guidance: "This is abnormal — investigate before retrying.",
      };
    case "merge_refused":
      return {
        key: "merge_refused",
        variant: "danger",
        icon: "fa-ban",
        heading: "Merge refused",
        guidance: null,
      };
    case "invalid_request":
      return {
        key: "invalid_request",
        variant: "danger",
        icon: "fa-exclamation-triangle",
        heading: "Invalid merge request",
        guidance: "Start over and correct the entities.",
      };
    case "operation_failed":
      return {
        key: "operation_failed",
        variant: "danger",
        icon: "fa-exclamation-triangle",
        heading: "Merge failed",
        guidance: null,
      };
    default:
      return {
        key: "generic",
        variant: "danger",
        icon: "fa-exclamation-triangle",
        heading: "Merge could not be completed",
        guidance: null,
      };
  }
}

function SuccessPanel({ result, onReset }) {
  const alreadyMerged = result?.already_merged === true;

  return (
    <div>
      {alreadyMerged ? (
        <div className="alert alert-info" data-testid="result-already-merged">
          <i className="fa fa-info-circle me-2" />
          <strong>Already merged.</strong> {result.note}
        </div>
      ) : (
        <div className="alert alert-success" data-testid="result-success">
          <i className="fa fa-check-circle me-2" />
          <strong>Merge complete.</strong> The source was merged into the destination.
        </div>
      )}

      <p className="text-muted" data-testid="result-caches-note">
        Search results and cached listings refresh in the background.
      </p>

      {result && Object.keys(result).length > 0 && (
        <pre className="bg-light p-3 rounded" style={{ fontSize: "0.85em", maxHeight: "220px", overflow: "auto" }}>
          {JSON.stringify(result, null, 2)}
        </pre>
      )}

      <button type="button" className="btn btn-primary" onClick={onReset}>
        Start another merge
      </button>
    </div>
  );
}

function ErrorPanel({ outcome, onRetry, onReset }) {
  const presentation = errorPresentation(outcome);

  return (
    <div>
      <div className={`alert alert-${presentation.variant}`} data-testid={`result-error-${presentation.key}`}>
        <i className={`fa ${presentation.icon} me-2`} />
        <strong>{presentation.heading}.</strong>
        {presentation.guidance && <div className="mt-2">{presentation.guidance}</div>}
        {outcome.error && (
          <div className="mt-2 fst-italic" data-testid="result-error-message">
            {outcome.error}
          </div>
        )}
      </div>

      <div className="d-flex gap-2">
        <button type="button" className="btn btn-secondary" onClick={onReset}>
          Start over
        </button>
        {outcome.canRetry && (
          <button type="button" className="btn btn-danger" onClick={onRetry} data-testid="result-retry">
            Retry
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * MergeResult — terminal step. Renders the normalized outcome from the merge
 * runner: success (incl. the idempotent already-merged note) or a structured
 * failure/stall whose heading, guidance, and Retry affordance follow the
 * normalizer's classification.
 */
function MergeResult({ outcome, onRetry, onReset }) {
  if (!outcome) return null;
  if (outcome.ok) return <SuccessPanel result={outcome.result} onReset={onReset} />;
  return <ErrorPanel outcome={outcome} onRetry={onRetry} onReset={onReset} />;
}

export default MergeResult;
